import React, { Component } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import { MDText } from 'i18n-react';
import { Calendar, DateRange } from 'react-date-range';
import moment from 'moment';
import classNames from 'classnames';

import { DateFormats } from '@adsk/bim360-matrix-react-helpers/es/consts';
import AnchorButton from '@adsk/bim360-matrix-react-anchor-button';
import ToggleButton from '@adsk/bim360-matrix-react-toggle-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';

import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import scrollElementIntoView from '@adsk/bim360-matrix-react-helpers/es/scrollElementIntoView';
import { deprecateComponent } from '@adsk/bim360-matrix-react-helpers/es/deprecate';

import calendarSvg from '@adsk/bim360-matrix-react-assets/calendar.svg';

import './_DatePicker.scss';

/**
 * Returns the default date format
 * @param date
 * @returns {string}
 */
export const defaultDateFormat = date => {
  if (!date._isAMomentObject) {
    date = moment(date);
  }

  return new Date().getFullYear() === date.year()
    ? DateFormats.SHORT_DATE_NO_YEAR_FORMAT
    : DateFormats.SHORT_DATE_FORMAT;
};

/**
 * DatePicker
 */
export class DatePicker extends Component {
  /**
   * constructor
   */
  constructor(props) {
    super(props);
    deprecateComponent('DatePicker', 'DateSelector');
    this.state = {
      open: props.openOnMount && !props.readOnly,
      shouldClose: false,
      selectedAction: null,
      actions: props.actions ? this._normalizeActions(props.actions) : [],
      ...this.initializeDateStateObject(props)
    };
  }

  /**
   *
   * @param dateA
   * @param dateB
   * @returns {boolean}
   * @private
   */
  _isSameDate(dateA, dateB) {
    return (
      dateA === dateB || (dateA && dateB && dateA.isSame && dateB.isSame && dateA.isSame(dateB))
    );
  }

  _isDateSelected() {
    return this.props.placeholder !== this.state.selectedValue;
  }

  _normalizeActions(actions) {
    return actions.map(action => ({
      ...action,
      id: action.label,
      isSelected: action.isSelected || noop
    }));
  }

  /**
   * componentWillReceiveProps - handles date props changes when received from preview-next/previous
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      (!this._isSameDate(this.state.startDate, nextProps.date) &&
        !(
          this._isSameDate(this.state.startDate, nextProps.startDate) &&
          this._isSameDate(this.state.endDate, nextProps.endDate)
        )) ||
      this.props.placeholder !== nextProps.placeholder
    ) {
      this.setState(this.initializeDateStateObject(nextProps));
    }

    if (nextProps.actions !== this.props.actions) {
      this.setState({ actions: this._normalizeActions(nextProps.actions) });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const compHolderComponent = this.compHolderRef;
    const containerComponent = this.props.scrollableContainer;

    if (containerComponent && this.state.open) {
      if (compHolderComponent) {
        scrollElementIntoView(compHolderComponent, containerComponent);
      }
    }

    if (prevState.open && !this.state.open) {
      this.props.onClose();
    } else if (!prevState.open && this.state.open) {
      this.props.onOpen();
    }
  }

  /**
   * Initializes date-related state object
   */
  initializeDateStateObject(props) {
    const stateObject = {};

    // Initialize selectedValue
    if (props.date) {
      stateObject.selectedValue = this.parseDate(props.date);
      stateObject.startDate = moment(props.date); // for single-date-picker (this.props.range===false) this is used as the date state
    } else if (props.startDate && props.endDate) {
      stateObject.selectedValue = `${this.parseDate(props.startDate)} - ${this.parseDate(
        props.endDate
      )}`;
      stateObject.startDate = moment(props.startDate);
      stateObject.endDate = moment(props.endDate);
    } else {
      stateObject.selectedValue = props.placeholder;
      stateObject.startDate = null;
      stateObject.endDate = null;
    }

    return {
      ...stateObject,
      ...this.getActionState(
        stateObject.startDate,
        stateObject.endDate,
        (this.state && this.state.actions) ||
          (props.actions && this._normalizeActions(props.actions))
      )
    };
  }

  /**
   * handle click outside the date picker
   */
  handleClickOutside() {
    this.setState({ open: false, shouldClose: false });
    if (this.props.onClickOutside) this.props.onClickOutside();
  }

  /**
   * handle date range range select
   */
  handleDateRangeChange(date) {
    const start = this.parseDate(date.startDate);
    const end = this.parseDate(date.endDate);

    if (
      this.parseDate(this.state.startDate) === start &&
      this.parseDate(this.state.endDate) === end
    ) {
      return;
    }

    this.setState({
      ...this.getActionState(date.startDate, date.endDate),
      selectedValue: `${start} - ${end}`,
      startDate: date.startDate,
      endDate: date.endDate,
      shouldClose: !this.state.shouldClose,
      open: !this.state.shouldClose
    });

    if (this.props.onChange) {
      this.props.onChange({ startDate: date.startDate, endDate: date.endDate });
    }
  }

  /**
   * parse date to string
   */
  parseDate(date) {
    return DatePicker.parseDateByFormat(date, this.props.format);
  }

  /**
   * DatePicker creates moment objects with only dates and no time. This function adds the current hours, minutes and seconds
   * @param {moment} date - Moment object
   * @returns {moment}
   */
  addCurrentTimeToDate(date) {
    const now = moment();
    date
      .add(now.hours(), 'h')
      .add(now.minutes(), 'm')
      .add(now.seconds(), 's');

    return date;
  }

  getActionState(startDate, endDate, actions = this.state ? this.state.actions : []) {
    return {
      selectedAction: (
        actions.filter(action => action.isSelected(moment(startDate), moment(endDate)))[0] || {}
      ).id
    };
  }

  /**
   * Clears input value and selection
   * Because of how the third-party comp works, we need 2 consecutive setState.
   *  - On ranged Datepicker resetting the dates toggles the onChange, so we need to reset the selectedValue afterwards
   *  - On single Datepicker resetting/changing the date doesn't trigger anything inside the component, so we need to unmount&mount it for the child constructor to run
   */
  clear() {
    const dateState = this.props.range
      ? { startDate: moment(), endDate: moment() }
      : { startDate: moment() };

    const actionState = this.getActionState(dateState.startDate, dateState.endDate);

    if (this.props.range) {
      this.setState(
        {
          ...dateState,
          ...actionState
        },
        () => {
          if (this.props.placeholder) {
            this.setState({ selectedValue: this.props.placeholder });
          }
        }
      );
    }

    this.setState(
      {
        ...dateState,
        ...actionState,
        selectedValue: this.props.placeholder,
        open: false,
        shouldClose: false
      },
      () => {
        this.setState({ open: true });
      }
    );

    this.props.onChange(null);
  }

  /**
   * date range comp.
   */
  _renderDateRange() {
    return (
      <DateRange
        minDate={this.props.minDate || (this.props.disablePastDates ? moment() : null)}
        maxDate={this.props.maxDate || (this.props.disableFutureDates ? moment() : null)}
        startDate={this.state.startDate}
        endDate={this.state.endDate}
        onChange={this.handleDateRangeChange.bind(this)}
        calendars={1}
        firstDayOfWeek={1}
        theme={this.state.theme}
      />
    );
  }

  /**
   * handle calendar (single-date DatePicker) change
   * @param {moment} date - Moment object (received from DatePicker)
   */
  handleCalendarChange(date) {
    // Currently we're adding the current time to the date so the BE will not block dates set for today
    // This will (probably) be changed in the future - TODO: https://jira.autodesk.com/browse/BTLV-1452
    const adjustedDate = this.addCurrentTimeToDate(date);
    const newDate = this.parseDate(adjustedDate);

    if (this.parseDate(this.state.startDate) === newDate) {
      return;
    }

    this.setState({
      ...this.getActionState(adjustedDate),
      selectedValue: newDate,
      startDate: adjustedDate,
      open: false
    });

    if (this.props.onChange) {
      this.props.onChange(adjustedDate);
    }
  }

  /**
   * calendar comp.
   */
  _renderCalendar() {
    return (
      <Calendar
        minDate={this.props.minDate || (this.props.disablePastDates ? moment() : null)}
        maxDate={this.props.maxDate || (this.props.disableFutureDates ? moment() : null)}
        date={this.state.startDate}
        onChange={this.handleCalendarChange.bind(this)}
        calendars={1}
        firstDayOfWeek={1}
        theme={this.state.theme}
      />
    );
  }

  _renderActions() {
    if (this.state.actions) {
      return (
        <div>
          <div className="DatePicker__divider" />
          <div className="DatePicker__actions">
            {this.state.actions.map(action => (
              <ToggleButton
                key={action.id}
                label={action.label}
                checked={action.id === this.state.selectedAction}
                onChange={checked => {
                  this.setState({
                    open: false,
                    selectedAction: checked || !action.canDeselect ? action.id : null
                  });
                  action.onToggle(checked);
                }}
              />
            ))}
          </div>
        </div>
      );
    } else {
      return null;
    }
  }

  /**
   * date picker popup
   */
  _renderPopup() {
    const component = this.props.range ? this._renderDateRange() : this._renderCalendar();
    const compHolderClasses = classNames('DatePicker__comp-holder', {
      'DatePicker__comp-holder--icon-only': this.props.iconOnly
    });

    return (
      <div
        className={compHolderClasses}
        ref={node => {
          this.compHolderRef = node;
        }}
      >
        {component}
        {this._renderActions()}
        {!this.props.hideClearDateButton && <div className="DatePicker__divider" />}
        {!this.props.hideClearDateButton && (
          <AnchorButton
            className="DatePicker__reset"
            onClick={() => {
              this.clear();
            }}
          >
            {this.props.clearDatesLabel
              ? this.props.clearDatesLabel
              : this.props[TSymbolProp].translate('CLEAR_DATES')}
          </AnchorButton>
        )}
      </div>
    );
  }

  /**
   * render
   */
  render() {
    const inputClasses = classNames(
      'DatePicker__input',
      { 'DatePicker__input--icon-only': this.props.iconOnly },
      { 'DatePicker__input--default': !this.props.underlined },
      { 'DatePicker__input--underlined': this.props.underlined },
      { 'DatePicker__input--with-icon': !this.props.noIcon },
      { 'DatePicker__input--disabled': this.props.disabled },
      { 'DatePicker__input--is-open-underline': this.props.underlined && this.state.open },
      { 'DatePicker__input--selected': this._isDateSelected() },
      { 'DatePicker__input--read-only': this.props.readOnly },
      { 'DatePicker__input--invalid': !this.props.valid }
    );

    const svgClasses = classNames('DatePicker__icon', {
      'DatePicker__icon--icon-only': this.props.iconOnly,
      'DatePicker__icon--underlined': this.props.underlined,
      'DatePicker__icon--disabled': this.props.disabled
    });
    const inputHolderClasses = classNames('DatePicker__input-holder', {
      'DatePicker__input-holder--open': this.state.open,
      'DatePicker__input-holder--icon-only': this.props.iconOnly,
      'DatePicker__input-holder--read-only': this.props.readOnly
    });

    const onClick =
      this.props.disabled || this.props.readOnly
        ? () => {}
        : () => {
          this.setState({ open: !this.state.open });
        };
    return (
      <div className="DatePicker">
        <div className={inputHolderClasses} onClick={onClick}>
          {this.props.noIcon ? null : (
            <SvgIcon className={svgClasses} transform="translate(0 0.5)">
              {calendarSvg}
            </SvgIcon>
          )}
          <div tabIndex="0" className={inputClasses}>
            {this.state.selectedValue}
          </div>
        </div>

        {this.state.open ? this._renderPopup() : null}
      </div>
    );
  }
}

/**
 * Parse date object by format
 * @param date - date object
 * @param format - string format
 * @returns {string} - The date as a string
 */
DatePicker.parseDateByFormat = (date, format) => {
  if (date === null) return '';

  if (!date._isAMomentObject) {
    date = moment(date);
  }

  if (format) {
    return date.format(format);
  } else {
    return date.format(defaultDateFormat(date));
  }
};

DatePicker.defaultProps = {
  noIcon: false,
  openOnMount: false,
  hideClearDateButton: false,
  iconOnly: false,
  readOnly: false,
  onClose: noop,
  onOpen: noop,
  valid: true
};

DatePicker.propTypes = {
  /**
   * Whether to use a calendar with range date selection
   */
  range: PropTypes.bool,
  /**
   * Disable selection of passed dates
   */
  disablePastDates: PropTypes.bool,
  /**
   * Disable selection of future dates
   */
  disableFutureDates: PropTypes.bool,
  /**
   * Allow selection of dates after minDate
   */
  minDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(moment)]),
  /**
   * Allow selection of dates before maxDate
   */
  maxDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(moment)]),
  /**
   * whether to show 'clear dates' button
   */
  hideClearDateButton: PropTypes.bool,
  /**
   * Use an input with underline style
   */
  underlined: PropTypes.bool,
  /**
   * Shows only date picker icon instead of the whole field
   */
  iconOnly: PropTypes.bool,
  /**
   * A callback function for a date select changed event
   */
  onChange: PropTypes.func,
  /**
   * A placeholder text when no date is selected
   */
  placeholder: PropTypes.string,
  /**
   * A selected start date for a calendar with range data (should be used with range=true)
   */
  startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(moment)]),
  /**
   * A selected end date for a calendar with range data (should be used with range=true)
   */
  endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(moment)]),
  /**
   * A selected date for calendar with single date select (should be used with range=false or range with default value)
   */
  date: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(moment)]),
  /**
   * A boolean to determine wether or not to show the date picker icon
   */
  noIcon: PropTypes.bool,
  /**
   * Date format. Should be a [moment.js](https://momentjs.com/docs/#/parsing/string-format/) string format
   */
  format: PropTypes.string,
  /**
   * Label for the clear dates button
   */
  clearDatesLabel: PropTypes.string,
  /**
   * Boolean for disabling the component
   */
  disabled: PropTypes.bool,
  /**
   * Open the dropdown from outside
   */
  openOnMount: PropTypes.bool,
  /**
   * An optional function to be ran when menu get closed or click outside
   */
  onClickOutside: PropTypes.func,
  /**
   * Element that contains the component and has scrolling vertically enabled
   */
  scrollableContainer: PropTypes.object,
  /**
   * Read only mode, default false
   */
  readOnly: PropTypes.bool,
  /**
   * A list of custom actions to show that should correspond to date values.
   */
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The label to show for the action
       */
      label: PropTypes.string.isRequired,
      /**
       * The action to do when toggling that action
       * (isSelected) => {}
       */
      onToggle: PropTypes.func.isRequired,
      /**
       * Whether or not you can deselect the action once it has been select
       */
      canDeselect: PropTypes.bool,
      /**
       * Callback that's invoked to determine if the action is selected.
       * When range === true => (startDate, endDate) => {}
       * When range === false => (date) => {}
       */
      isSelected: PropTypes.func
    })
  ),
  /**
   * A callback function for the event of closing the date picker
   */
  onClose: PropTypes.func,
  /**
   * A callback function for the event of opening the date picker
   */
  onOpen: PropTypes.func,
  /**
   * Flag indicating whether or not the input is in a valid or invalid state.
   */
  valid: PropTypes.bool,
  /**
   * The locale for the texts in the component
   */
  locale: PropTypes.string
};

export default withLocale(DatePicker, ({ locale }) => new MDText(require(`./i18n/${locale}.json`)));
